import { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';
import {
  FilterOnEmptyState,
  LoadingSpinner,
  ViewMoreCard,
  NoFlyersAvailableEmptyState,
  NoFlyersAvailableSectionEmptyState,
} from '@/components';
import { FlyerType } from '@/config/constants';
import { AllFlyerFilters } from '@/graphql/types';
import { filterFacetsVar, flyerboardActiveFiltersVar } from '@/graphql/state';
import { CommunityDisclaimerModal } from '@/components/CommunityDisclaimerModal/CommunityDisclaimerModal';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import {
  useEffectOnMount,
  useFlyerboardPagination,
  useIsVisible,
} from '@/hooks';
import AuthenticationContext from '@/context/AuthenticationContext';
import { FlyerList } from './FlyerList';
import { OtherSchoolFlyers } from './OtherSchoolFlyers';
import styles from './FlyerboardPagination.styles';
import colors from '@/theme/colors';
import { BodyLargeEmphasized } from '@/design-system';

type Props = {
  trackId: string;
  flyerboardFilters: AllFlyerFilters;
  flyerType: string;
  audienceId?: string;
  districtId?: string | null;
};

export const FlyerboardPagination: React.FC<Props> = ({
  trackId,
  flyerboardFilters,
  flyerType,
  audienceId,
  districtId,
}) => {
  const { viewWholeCommunityFlyers } = useContext(UserPreferencesContext);
  const [showCommDisclaimerModal, setShowCommDisclaimerModal] =
    useState<boolean>(false);
  const [allDistrictSchools, setAllDistrictSchools] = useState<boolean>(false);
  const [hasOtherSchoolFlyers, setHasOtherSchoolFlyers] = useState<boolean>(true);
  const ref = useRef<HTMLDivElement | null>(null);
  const isPageVisible = useIsVisible(ref);
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const { isAuthenticated } = useContext(AuthenticationContext);

  const {
    page,
    setPage,
    pages,
    isPaginationEnd,
    flyers,
    resetPagination,
    favoriteFlyer,
    loading,
    total,
    dataNotFound,
  } = useFlyerboardPagination({
    trackId,
    flyerboardFilters,
    flyerType,
    audienceId,
    districtId,
    isPageVisible,
  });

  const showCommunityHeader = !flyerboardFilters.isNew && flyerType === FlyerType.community;

  const shouldShowWholeCommunity = useMemo(
    () =>
      flyerType === FlyerType.community &&
      viewWholeCommunityFlyers &&
      !allDistrictSchools &&
      isAuthenticated,
    [flyerType, viewWholeCommunityFlyers, allDistrictSchools, isAuthenticated],
  );

  const isFiltering = useMemo(
    () =>
      flyerboardActiveFilters.grades_age.length > 0 ||
      flyerboardActiveFilters.types.length > 0,
    [flyerboardActiveFilters],
  );

  const clearFilterFacets = () => {
    filterFacetsVar({
      categories: [],
      gradesOrAges: [],
    });
  };

  const seeMoreFlyersButton = useMemo(() => {
     // check if the user accept the community disclaimer
    if(flyerboardFilters.isNew || viewWholeCommunityFlyers !== null || !isAuthenticated || flyerType === FlyerType.school) {
      return false;
    }
    return true
  }, [flyerType, viewWholeCommunityFlyers, isAuthenticated, flyerboardFilters.isNew])

  useEffectOnMount(() => {
    clearFilterFacets();
  });

  useEffect(() => {
    if (!isPageVisible) return;
    setAllDistrictSchools(false);
    clearFilterFacets();
    resetPagination();
  }, [flyerboardFilters, flyerboardActiveFilters, isPageVisible]);

  useEffect(() => {
    // check if should show the all school districts section
    if (shouldShowWholeCommunity && (page === pages || isPaginationEnd)) {
      setAllDistrictSchools(true);
    }
  }, [page, pages, isPaginationEnd]);

  const resetFilters = () => {
    flyerboardActiveFiltersVar({
      grades_age: [],
      types: [],
    });
  };

  const nextPage = () => {
    setPage(prevPage => prevPage + 1);
  };

  const onFavoriteFlyer = (flyerId: number) => favoriteFlyer(flyerId);

  const onSeeMoreFlyersClick = () => {
    setShowCommDisclaimerModal(true);
  };

  const onConfirmCommDisclaimer = () => {
    setAllDistrictSchools(true);
    onCancelCommDisclaimer();
  };

  const onCancelCommDisclaimer = () => {
    setShowCommDisclaimerModal(false);
  };

  const EmptyState = () => {
    if (!loading && dataNotFound) {
      if (isFiltering) {
        return <FilterOnEmptyState onClick={resetFilters} />;
      }

      if (flyerboardFilters.isNew ||
        trackId === 'districtFlyerboard' ||
        (flyerType === FlyerType.community && !hasOtherSchoolFlyers)) {
        return <NoFlyersAvailableEmptyState />;
      }

      return flyerType === FlyerType.school ? <NoFlyersAvailableEmptyState /> : <NoFlyersAvailableSectionEmptyState />;
    }

    return null;
  };

  return (
    <main className={styles.container} ref={ref}>
      <div className={styles.section}>
        {loading && flyers.length === 0 && <LoadingSpinner />}
        {showCommunityHeader && (
          <div className={styles.communityHeader}>
            <BodyLargeEmphasized color={colors.slate} >
              Community Through My School(s)
             </BodyLargeEmphasized>
          </div>
        )}
        <EmptyState />
        {flyers.length > 0 && (
          <FlyerList
            flyers={flyers}
            nextPage={nextPage}
            onFavoriteFlyer={onFavoriteFlyer}
            total={total}
            trackId={trackId}
            audienceId={audienceId}
            districtId={districtId}
          />
        )}
      </div>
      {seeMoreFlyersButton && (
        <ViewMoreCard
          title="MY WHOLE COMMUNITY"
          description="Open up my whole community to view all opportunities fitting for my child."
          buttonLabel="View my whole community"
          onClick={onSeeMoreFlyersClick}
        />
      )}
      {flyerType === FlyerType.community &&
        !flyerboardFilters.isNew &&
        allDistrictSchools &&
        isAuthenticated && (
          <OtherSchoolFlyers
            flyerType={flyerType}
            flyerboardFilters={flyerboardFilters}
            trackId={trackId}
            audienceId={audienceId}
            setHasOtherSchoolFlyers={setHasOtherSchoolFlyers}
            hasMyCommSchoolFlyers={!dataNotFound}
          />
        )}
      <CommunityDisclaimerModal
        isOpen={showCommDisclaimerModal}
        onConfirm={onConfirmCommDisclaimer}
        onCancel={onCancelCommDisclaimer}
      />
    </main>
  );
};
